import {
  booleanAttribute,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from "@angular/core";

import { focusMovedOutside } from "../utils/focus-moved-outside";

export type ClickOutsideReason = "outside-click" | "escape" | "focus-out";

@Directive({
  selector: "[appClickOutside]",
})
export class ClickOutsideDirective {
  @Output() clickOutside = new EventEmitter<ClickOutsideReason>();

  /**
   * Also emit "focus-out" when keyboard focus moves out of the host (see focusMovedOutside), so a
   * popup closes on Tab out the way it does on an outside click. Opt-in rather than always on: a
   * consumer's handler may discard state on dismissal, so each one decides whether tabbing past
   * should do the same (category-select, for one, keeps an unsaved name on "focus-out"). A mouse
   * press never emits "focus-out": it's an outside click, emitted on mouseup as before.
   */
  @Input({ transform: booleanAttribute }) appClickOutsideFocusOut = false;

  // Set on mousedown, read on mouseup. A press that starts inside the host (selecting text,
  // dragging a scrollbar) and releases outside it must not count as a dismissal — checking
  // only the mouseup target let that happen mid-interaction.
  private mouseDownInside = false;

  constructor(private eRef: ElementRef) {}

  @HostListener("document:mousedown", ["$event"])
  onMouseDown(event: MouseEvent | TouchEvent) {
    this.mouseDownInside = this.eRef.nativeElement.contains(event.target);
  }

  /** if the requirement for mat-calendar to not close is needed (currently calendar is not using this directive but a similar approach),
   * check git history for the x/y position version */
  @HostListener("document:mouseup", ["$event"])
  onClick(event: MouseEvent | TouchEvent) {
    const mouseUpInside = this.eRef.nativeElement.contains(event.target);
    if (!this.mouseDownInside && !mouseUpInside) {
      this.clickOutside.emit("outside-click");
    }
    this.mouseDownInside = false;
  }

  // Keyboard-only/screen-reader users have no other way to dismiss an appClickOutside panel
  // (see #4511 follow-up) — every consumer already wires (clickOutside) to a close handler
  // that's a no-op when it isn't currently open, so reusing the same output for Escape is
  // safe without any per-consumer changes. The reason is passed through (rather than reusing
  // a bare emit()) so a consumer that needs to tell the two apart — e.g. a detached/portaled
  // panel that intentionally ignores "outside-click" because the portal falls outside this
  // host's DOM subtree — still gets Escape.
  @HostListener("document:keydown.escape")
  onEscape() {
    this.clickOutside.emit("escape");
  }

  @HostListener("focusout", ["$event"])
  onFocusOut(event: FocusEvent) {
    if (this.appClickOutsideFocusOut && focusMovedOutside(event, this.eRef.nativeElement)) {
      this.clickOutside.emit("focus-out");
    }
  }
}
